import type { CatalogAgent } from "@bash/shared";
import { CascadeConfirmRequiredError } from "./skills";
import { authedFetch, getRequiredAccessToken } from "./client";
import { installItem, findMyItemBySlug, deleteItem } from "./items";

// ---------------------------------------------------------------------------
// Agents catalog endpoint
//
// Backed by `GET /api/items/agent/catalog`. Returns scope=system|marketplace
// agents projected to `CatalogAgent` shape with computed state-from-caller
// so the UI can dim install buttons + group cards without extra round-trips.
// ---------------------------------------------------------------------------

/**
 * Browse the agents catalog (system + marketplace).
 *
 * - `q`        — full-text-ish search over slug + metadata.name + metadata.description
 * - `category` — exact-match filter on metadata.category
 */
export async function listAgentsCatalog(
  params: {
    q?: string;
    category?: string;
    limit?: number;
    offset?: number;
  } = {}
): Promise<CatalogAgent[]> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("search", params.q);
  if (params.category) qs.set("category", params.category);
  if (typeof params.limit === "number") qs.set("limit", String(params.limit));
  if (typeof params.offset === "number")
    qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  const res = await authedFetch(`/api/items/agent/catalog${suffix}`);
  const body = (await res.json()) as { items: CatalogAgent[] };
  return body.items ?? [];
}

/** Install a marketplace/system agent — hits POST /api/items/agent/install/:slug. */
export async function installAgent(
  slug: string
): Promise<{ ok: true; slug: string }> {
  const result = await installItem("agent", slug);
  return { ok: true, slug: result.slug };
}

/**
 * Uninstall an agent by slug.
 *
 * Legacy callers pass a slug; the new server endpoint takes an item id, so we
 * resolve via GET /api/items/agent/mine first, then DELETE /api/items/agent/:id.
 */
export async function uninstallAgent(slug: string): Promise<{ ok: true }> {
  const mine = await findMyItemBySlug("agent", slug);
  if (!mine) throw new Error(`Agent not installed: ${slug}`);
  await deleteItem("agent", mine.id);
  return { ok: true };
}

// ---------------------------------------------------------------------------
// Cascade-aware agent uninstall (client-side preflight + new id-based DELETE)
// ---------------------------------------------------------------------------

export interface AgentUsage {
  affected_abilities: Array<{ slug: string; name: string }>;
}

/**
 * "Usage" of an installed agent = the abilities that bundle it. Derived
 * client-side from the `bundling_ability_slugs` field on the user's item.
 *
 * Replaces the legacy `/api/agents/install/:slug/usage` endpoint.
 */
export async function getAgentUsage(slug: string): Promise<AgentUsage> {
  const mine = await findMyItemBySlug("agent", slug);
  if (!mine) return { affected_abilities: [] };
  return {
    affected_abilities: mine.bundling_ability_slugs.map((s) => ({
      slug: s,
      name: s,
    })),
  };
}

export interface UninstallAgentResult {
  ok: true;
  removed_from_abilities: string[];
}

/**
 * Cascade-aware uninstall — same semantics as `uninstallSkillWithCascade`.
 * The new canonical DELETE always cascades; we replicate the legacy modal
 * gate client-side using `bundling_ability_slugs`.
 */
export async function uninstallAgentWithCascade(
  slug: string,
  opts: { confirmCascade?: boolean } = {}
): Promise<UninstallAgentResult> {
  await getRequiredAccessToken();

  const mine = await findMyItemBySlug("agent", slug);
  if (!mine) throw new Error(`Agent not installed: ${slug}`);

  const bundles = mine.bundling_ability_slugs;
  if (bundles.length > 0 && !opts.confirmCascade) {
    throw new CascadeConfirmRequiredError(
      bundles.map((s) => ({ slug: s, name: s }))
    );
  }

  await deleteItem("agent", mine.id);
  return { ok: true, removed_from_abilities: bundles };
}
